import { useState } from "react";
import { useTagsWithCounts } from "../api/items.js";
import {
  useTagSchedules,
  useUpsertTagSchedule,
  useDeleteTagSchedule,
  useTagScheduleConflicts,
} from "../api/schedules.js";
import { BackButton } from "../components/BackButton.js";
import { CronPicker } from "../components/CronPicker.js";
import { ErrorState, Loading } from "../components/Loading.js";
import { useMe } from "../api/items.js";

// Страница «Расписания». Owner/admin задаёт cron на конкретный тег → все items
// с этим тегом наследуют расписание (если у них нет explicit override).
//
// Конфликт: item с ≥2 совпадающими tag-schedule fallback'нется на globalDefault
// и в Detail покажет warning «конфликт тегов». UI здесь предупреждает заранее
// перед сохранением (preview-conflicts endpoint).

export function SchedulesPage() {
  const me = useMe();
  const tags = useTagsWithCounts();
  const schedules = useTagSchedules();
  const [editing, setEditing] = useState<string | null>(null);

  if (me.isLoading || tags.isLoading || schedules.isLoading) return <Loading />;
  if (me.isError) return <ErrorState error={me.error} />;
  if (!me.data || me.data.role === "viewer") {
    return (
      <div className="p-3">
        <BackButton to="/" />
        <div className="mt-3 rounded-2xl bg-bg-secondary p-6 text-center text-hint">
          Раздел доступен только админам и владельцу
        </div>
      </div>
    );
  }

  // Map для O(1) lookup тегов с расписанием.
  const scheduleByTag = new Map((schedules.data ?? []).map((s) => [s.tag, s]));

  return (
    <div className="space-y-3 p-3">
      <BackButton to="/" />
      <h1 className="text-lg font-bold">Расписания по тегам</h1>
      <div className="rounded-lg bg-bg-secondary p-3 text-xs text-hint">
        Cron на тег → все items с этим тегом получают расписание. Если у item есть
        explicit override (на странице Detail) — он бьёт любые tag-расписания.
        При ≥2 matching tag-schedules item уходит на глобальный default и в Detail
        показывает warning «конфликт».
      </div>

      <div className="space-y-1.5">
        {(tags.data ?? []).map((t) => {
          const sched = scheduleByTag.get(t.tag);
          return (
            <button
              key={t.tag}
              type="button"
              onClick={() => setEditing(t.tag)}
              className="flex w-full items-center justify-between rounded-xl bg-bg-secondary p-3 text-left transition active:scale-[0.99]"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{t.tag}</div>
                <div className="text-xs text-hint">{t.count} items</div>
              </div>
              <div className="text-right">
                {sched ? (
                  <code className="rounded bg-bg px-1.5 py-0.5 text-xs">{sched.cron}</code>
                ) : (
                  <span className="text-xs text-hint">—</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {editing && (
        <ScheduleEditorModal
          tag={editing}
          currentCron={scheduleByTag.get(editing)?.cron ?? null}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ScheduleEditorModal({
  tag,
  currentCron,
  onClose,
}: {
  tag: string;
  currentCron: string | null;
  onClose: () => void;
}) {
  const upsert = useUpsertTagSchedule();
  const del = useDeleteTagSchedule();
  const conflicts = useTagScheduleConflicts(tag);

  const handleSave = (cron: string | null) => {
    if (!cron) return;
    upsert.mutate(
      { tag, cron },
      {
        onSuccess: onClose,
        onError: (err) => alert(`Ошибка: ${(err as Error).message}`),
      },
    );
  };

  const handleDelete = () => {
    if (!confirm(`Удалить расписание для тега "${tag}"?`)) return;
    del.mutate(tag, {
      onSuccess: onClose,
      onError: (err) => alert(`Ошибка: ${(err as Error).message}`),
    });
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/50 p-3"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md space-y-3 rounded-2xl bg-bg p-3"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-hint">Тег</div>
            <div className="text-base font-bold">{tag}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-2xl leading-none text-hint"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        {conflicts.data && conflicts.data.length > 0 && (
          <div className="rounded-lg bg-destructive/15 p-3 text-xs">
            <div className="font-semibold text-destructive">
              ⚠ Конфликт у {conflicts.data.length} items
            </div>
            <div className="mt-1 text-hint">
              У этих items есть другой тег с расписанием. После сохранения они
              перейдут на глобальный default (нужно установить explicit override
              в Detail):
            </div>
            <ul className="mt-1 space-y-0.5">
              {conflicts.data.slice(0, 5).map((c) => (
                <li key={c.id} className="truncate">
                  • {c.displayName} <span className="text-hint">({c.otherTagsWithSchedule.join(", ")})</span>
                </li>
              ))}
              {conflicts.data.length > 5 && (
                <li className="text-hint">…ещё {conflicts.data.length - 5}</li>
              )}
            </ul>
          </div>
        )}

        <CronPicker value={currentCron} onChange={handleSave} />

        {currentCron && (
          <button
            type="button"
            disabled={del.isPending}
            onClick={handleDelete}
            className="w-full rounded-xl bg-bg-secondary py-2 text-sm font-medium text-destructive disabled:opacity-50"
          >
            {del.isPending ? "..." : "Удалить расписание"}
          </button>
        )}
      </div>
    </div>
  );
}
